import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Mail, Phone, Globe, MapPin, Linkedin, ExternalLink } from "lucide-react";

interface CardData {
  card: any;
  links: any[];
  photos: any[];
}

export default function CardSite() {
  const { personalId } = useParams<{ personalId: string }>();
  const [data, setData] = useState<CardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!personalId) return;
    api.get<CardData>(`/api/v1/public/by-id/${personalId}`)
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));

    // Track visit
    api.post("/api/v1/analytics", { pageUrl: `/u/${personalId}`, userId: personalId }).catch(() => {});
  }, [personalId]);

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
    </div>
  );

  if (error || !data) return (
    <div className="flex flex-col items-center justify-center min-h-screen gap-4">
      <h1 className="text-2xl font-bold">Card Not Found</h1>
      <p className="text-muted-foreground">{error || "This card does not exist."}</p>
    </div>
  );

  const { card, links, photos } = data;
  const name = card.name_en || card.name_tc || card.name_sc || "Unknown";
  const title = card.title_en || card.title_tc || card.title_sc || "";
  const company = card.company_en || card.company_tc || card.company_sc || "";
  const bio = card.bio_en || card.bio_tc || card.bio_sc || "";

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-orange-50">
      <div className="max-w-lg mx-auto px-4 py-8">
        {/* Profile card */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Cover */}
          {card.cover_photo_url ? (
            <div className="h-40 bg-gradient-to-r from-primary to-pink-500">
              <img src={card.cover_photo_url} alt="" className="w-full h-full object-cover" />
            </div>
          ) : (
            <div className="h-40 bg-gradient-to-r from-primary to-pink-500" />
          )}

          {/* Avatar & info */}
          <div className="px-6 pb-6 -mt-12 relative">
            <div className="w-24 h-24 rounded-full border-4 border-white overflow-hidden bg-white shadow-md">
              {card.profile_photo_url ? (
                <img src={card.profile_photo_url} alt={name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-primary to-pink-500 flex items-center justify-center">
                  <span className="text-white text-2xl font-bold">{name.charAt(0)}</span>
                </div>
              )}
            </div>

            <h1 className="mt-4 text-2xl font-bold text-gray-900">{name}</h1>
            {title && <p className="text-primary font-medium">{title}</p>}
            {company && <p className="text-gray-600 text-sm">{company}</p>}
            {bio && <p className="mt-3 text-gray-600 text-sm leading-relaxed">{bio}</p>}

            {/* Contact info */}
            <div className="mt-5 space-y-2.5">
              {card.email && (
                <a href={`mailto:${card.email}`} className="flex items-center gap-3 text-sm text-gray-700 hover:text-primary transition-colors">
                  <Mail className="w-4 h-4 text-primary" /> {card.email}
                </a>
              )}
              {card.phone && (
                <a href={`tel:${card.phone}`} className="flex items-center gap-3 text-sm text-gray-700 hover:text-primary transition-colors">
                  <Phone className="w-4 h-4 text-primary" /> {card.phone}
                </a>
              )}
              {card.website && (
                <a href={card.website} target="_blank" rel="noopener" className="flex items-center gap-3 text-sm text-gray-700 hover:text-primary transition-colors">
                  <Globe className="w-4 h-4 text-primary" /> {card.website}
                </a>
              )}
              {card.address_en && (
                <p className="flex items-center gap-3 text-sm text-gray-700">
                  <MapPin className="w-4 h-4 text-primary" /> {card.address_en}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Links */}
        {links.length > 0 && (
          <div className="mt-6 space-y-3">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider px-1">Links</h2>
            {links.map((link: any) => (
              <a
                key={link.id}
                href={link.url}
                target="_blank"
                rel="noopener"
                className="flex items-center gap-3 bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  {link.type === "linkedin" ? <Linkedin className="w-5 h-5 text-primary" /> : <ExternalLink className="w-5 h-5 text-primary" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">{link.label || link.url}</p>
                  {link.description && <p className="text-xs text-gray-500 truncate">{link.description}</p>}
                </div>
              </a>
            ))}
          </div>
        )}

        {/* Photos */}
        {photos.length > 0 && (
          <div className="mt-6">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider px-1 mb-3">Gallery</h2>
            <div className="grid grid-cols-2 gap-2">
              {photos.map((photo: any) => (
                <img key={photo.id} src={photo.url} alt={photo.caption || ""} className="rounded-lg w-full aspect-square object-cover" />
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-xs text-gray-400">Powered by <a href="/" className="text-primary hover:underline">card.buzz</a></p>
        </div>
      </div>
    </div>
  );
}
